import { useEffect } from "react"
import { useLocalStorage, usePreviousDistinct } from "react-use"
import { TipId, TipStatus } from "./types"

export type TipStatusChangeEventDetail = {
  id: TipId
  status: TipStatus
}

// Extending the CustomEventMap with our own tipStatusChange event key.
interface CustomEventMap {
  tipStatusChange: CustomEvent<TipStatusChangeEventDetail>
}

export function useTipStatus(id: TipId) {
  // Using raw value as TipStatus is just a string and it ensures
  // the value conforms to the raw strings already stored before
  // begining to use the useLocalStorage hook.
  const useLocalStorageReturn = useLocalStorage<TipStatus>(
    `tip-${id}`,
    undefined,
    { raw: true }
  )

  const [tipStatus, setTipStatus] = useLocalStorageReturn

  // Since using the hook in multiple places won't automatically sync state,
  // we improvise using custom events.
  const previousTipStatus = usePreviousDistinct(tipStatus)
  useEffect(() => {
    if (previousTipStatus && tipStatus && tipStatus !== previousTipStatus) {
      window.dispatchEvent(
        new CustomEvent<TipStatusChangeEventDetail>("tipStatusChange", {
          detail: {
            id,
            status: tipStatus
          }
        })
      )
    }
  }, [tipStatus])

  // Respond to tip status changing.
  useEffect(() => {
    function handler({ detail }: CustomEvent<TipStatusChangeEventDetail>) {
      if (detail.id === id && detail.status !== tipStatus) {
        setTipStatus(detail.status)
      }
    }

    window.addEventListener("tipStatusChange", handler)
    return () => window.removeEventListener("tipStatusChange", handler)
  }, [tipStatus])

  return useLocalStorageReturn
}

export default useTipStatus
